import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import styles from "./staticProducts.module.scss";
import Image from "next/image";
import Head from "next/head";
import HeroGradientTextWithIcon from "@/components/heroGradientTextWithIcon/heroGradientTextWithIcon";
import TitleWithDescriptionV2 from "@/components/titleWithDescriptionV2/titleWithDescriptionV2";
import { svgImages } from "@/lib/svgImages";
import { useTranslation } from "react-i18next";
import StaticVsDynamicV2 from "@/components/staticVsDynamicV2/staticVsDynamicV2";
import FaqWithCta from "@/components/faqWithCta/faqWithCta";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/navigation";
import { useWordPressApi } from "@/hooks/useWordPressApi";

const StaticProducts = () => {
  const router = useRouter();
  const [staticList, setStaticList] = useState([]);
  const [currentUrl, setCurrentUrl] = useState("");
  const [domainUrl, setDomainUrl] = useState("");
  const { t } = useTranslation();
  const cardsRef = useRef(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const { getProducts } = useWordPressApi();

  useEffect(() => {
    if (window.location.href) {
      setCurrentUrl(window.location.href);
      setDomainUrl(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    (async () => {
      var staticListArr = [];

      try {
        const data = await getProducts();

        if (data && Array.isArray(data)) {
          for (let index = 0; index < data.length; index++) {
            const element = data[index];
            if (element.acf && element.acf.isdynamic) {
            } else {
              staticListArr.push(element);
              setStaticList(staticListArr);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    })();
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const timer = setTimeout(() => {
      if (cardsRef.current && staticList.length > 0) {
        const cards = cardsRef.current.querySelectorAll(
          `.${styles.productCard}`
        );

        if (cards && cards.length > 0) {
          gsap.fromTo(
            cards,
            {
              opacity: 0,
              y: 10,
            },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              stagger: 0.3,
              ease: "power3.out",
              scrollTrigger: {
                trigger: cardsRef.current,
                start: "top center+=100",
                toggleActions: "play none none reverse",
              },
            }
          );
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [staticList]);

  const baseUrl = `https://${domainUrl || "qrhub.io"}`;
  const defaultOgImage = `${baseUrl}/images/featured_images/qrhub-featured.jpg`;
  const defaultOgUrl = currentUrl || `${baseUrl}${router.asPath || ""}`;

  return (
    <>
      <Head>
        <title>QRHUB - Static Products</title>
        <meta
          name="description"
          content="At QRHub, we offer simple static QR codes that store fixed information, making them perfect for sharing links, Wi-Fi credentials, or digital business cards."
        ></meta>
        <meta property="og:title" content="QRHUB - Static Products"></meta>
        <meta
          property="og:description"
          content="At QRHub, we offer simple static QR codes that store fixed information, making them perfect for sharing links, Wi-Fi credentials, or digital business cards."
        ></meta>
        <meta property="og:image" content={defaultOgImage}></meta>
        <meta property="og:url" content={defaultOgUrl}></meta>
      </Head>
      <div className={`${styles["staticProducts"]}`}>
        <HeroGradientTextWithIcon
          title="Static <span style='display: block;'>QR Codes</span>"
          subtitle=""
          description="Lock data in — use for  links, Wi-Fi, and vCards."
          image={
            <Image
              src="/images/products/static-qr-codes.svg"
              width={311}
              height={311}
              alt="Static QR Codes"
            />
          }
          buttonTitle={`Try for free`}
          buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL}
          target={"_blank"}
          isProduct={false}
          isSolution={false}
          isStatic={true}
        />

        <div className={`${styles["staticProductsList"]}`}>
          <div className="custom-container">
            <div className="inner">
              <TitleWithDescriptionV2
                title="Fixed Data,<span style='display: block;'> Fast Access</span>"
                description="We provide easy-to-use static QR codes that simplify how you share information. Static QR codes store fixed data that doesn't change after it's created."
              />
              <div
                className={`${styles["productsCards"]} d-flex-xl d-none-xs`}
                ref={cardsRef}
              >
                {staticList &&
                  staticList
                    .sort((a, b) => (a.id < b.id ? -1 : 1))
                    .map((item) => (
                      <div className={`${styles["productCard"]}`} key={item.id}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html:
                              svgImages[
                                `qr_${t(item.acf.hero_title)
                                  .toLowerCase()
                                  .replace(/[\s-]/g, "_")}`
                              ],
                          }}
                        />
                        <div
                          className={`${styles["productTitle"]} font-size-32`}
                        >
                          {item.acf.hero_title}
                        </div>
                        <div
                          className={`${styles["productDesc"]} font-size-20 line-height-content`}
                        >
                          {item.acf.hero_description}
                        </div>
                      </div>
                    ))}
              </div>
              <div
                className={`${styles.sliderContainer} ${styles["productsCards"]} d-none-xl d-flex-xs`}
              >
                <Swiper
                  modules={[Navigation, Autoplay, Pagination]}
                  centeredSlides={false}
                  loop={false}
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                    stopOnLastSlide: false,
                  }}
                  pagination={{
                    el: `.${styles.swiperPagination}`,
                    clickable: true,
                  }}
                  breakpoints={{
                    0: {
                      slidesPerView: 1.2,
                      spaceBetween: 10,
                      centeredSlides: false,
                    },
                    480: {
                      slidesPerView: 1.2,
                      spaceBetween: 10,
                      centeredSlides: false,
                    },
                    1025: {
                      slidesPerView: 3,
                      spaceBetween: 30,
                      centeredSlides: false,
                    },
                  }}
                  className={styles.featuresSwiperElement}
                  initialSlide={0}
                  grabCursor={true}
                >
                  {staticList &&
                    staticList.map((slide) => (
                      <SwiperSlide
                        key={slide.id}
                        className={styles.swiperSlide}
                      >
                        <div
                          className={`${styles["productCard"]}`}
                          key={slide.id}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                svgImages[
                                  `qr_${t(slide.acf.hero_title)
                                    .toLowerCase()
                                    .replace(/[\s-]/g, "_")}`
                                ],
                            }}
                          />
                          <div
                            className={`${styles["productTitle"]} font-size-32`}
                          >
                            {slide.acf.hero_title}
                          </div>
                          <div
                            className={`${styles["productDesc"]} font-size-20 line-height-content`}
                          >
                            {slide.acf.hero_description}
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                </Swiper>

                <div className={`${styles.swiperPagination}`}></div>
              </div>
            </div>
          </div>
        </div>
        <div className="custom-container">
          <div className="inner">
            <StaticVsDynamicV2 />
          </div>
        </div>
        <FaqWithCta
          title="Static vs. Dynamic <span style='display: block;'> QR Codes Which is Right for You?</span>"
          description="Explore the key differences to find the perfect solution for your business!"
          buttonTitle="Learn more"
          buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL}
          target={"_blank"}
        />
      </div>
    </>
  );
};

export default StaticProducts;
